import React, { useMemo } from "react";
import Chart2 from "./Chart2";
import PartialWrapper from "@client/components/wrappers/PartialWrapper";
import { useWsSubscription } from "@client/lib/wsSubscriptions";

interface Props {
  topicIds: string[];
  boolean?: boolean;
  /**
   * Živá hodnota (z device telemetrie) zobrazená v hlavičce MÍSTO throttlovaného MQTT průměru.
   * Sjednotí číslo v hlavičce s overview/diagramem. Platí jen pro single-topic graf; čára grafu
   * dál kreslí MQTT řadu (15s průměr), takže její konec se s tímto číslem nemusí přesně krýt.
   */
  liveValue?: number;
}

/**
 * Graf, který si SÁM zaregistruje WS subscription na své MQTT topicy (`mqttTopics` fragment).
 *
 * Proč: `PartialWrapper`/`useTopics` čte nickname, jednotku i hodnotu z `byTopic` (values store),
 * který server plní jen pro topicy s aktivní `mqttTopics` subscription. Na hlavní stránce ji dělá
 * page (App.tsx); na detailu zařízení page subscribuje jen device telemetrii — proto si ji nese
 * tato komponenta. Server na `subscribe` hned pošle initial dump hodnot včetně metadat.
 */
export const SubscribedChart: React.FC<Props> = ({ topicIds, boolean = false, liveValue }) => {
  // Fragment stabilní podle OBSAHU topicIds (ne identity pole) — jinak by se subscription
  // přeregistrovávala při každém renderu. Stejný vzor jako useTopics (join key + memo).
  const key = topicIds.join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fragment = useMemo(() => ({ mqttTopics: topicIds }), [key]);
  useWsSubscription(fragment);

  // Override hodnoty v hlavičce na živou telemetrii (0 W je validní → Number.isFinite).
  const valueOverrides = useMemo(
    () =>
      typeof liveValue === "number" && Number.isFinite(liveValue) && topicIds.length === 1
        ? { [topicIds[0]]: liveValue }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveValue, key]
  );

  return (
    <PartialWrapper topicIds={topicIds} valueOverrides={valueOverrides}>
      <Chart2 topicIds={topicIds} boolean={boolean} />
    </PartialWrapper>
  );
};
